import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { Card, CardBody, CardTitle, Alert } from "reactstrap";
import LoaderInner from "../../components/LoaderInner";
import { StateContext } from "../../utils/context";
import TenantContext from "../../utils/tenantContext";
import { getUser } from "../../utils/getUser";
import { getCookie } from "../../jwt/_helpers/cookie";
import {
  buildBillingPortalUrl,
  enrichSubscriptionDetails,
  getActiveTenantSubscription,
  getSubscriptionId,
  getSubscriptionStatusBadgeConfig,
  resolveCurrentPlan,
  userHasActiveSubscription,
} from "./planUtils";
import CurrentSubscriptionDetails from "./currentSubscriptionDetails";
import AvailablePlansSection from "./availablePlansSection";
import "./tenantCustomerPlans.css";

const TenantCustomerPlans = () => {
  const { currentUser, setCurrentUser } = useContext(StateContext);
  const { tenant } = useContext(TenantContext);
  const [plans, setPlans] = useState([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState(() =>
    getActiveTenantSubscription(currentUser)
  );
  const [billingInProgress, setBillingInProgress] = useState(false);

  useEffect(() => {
    setSubscriptionDetails(getActiveTenantSubscription(currentUser));
  }, [currentUser]);

  const handlePlansLoaded = useCallback(({ plans: items, user }) => {
    setPlans(items);
    if (user) {
      setSubscriptionDetails(getActiveTenantSubscription(user));
    }
  }, []);

  const activeSubscription = useMemo(
    () => enrichSubscriptionDetails(subscriptionDetails, plans),
    [subscriptionDetails, plans]
  );

  const currentPlan = useMemo(
    () => resolveCurrentPlan(plans, subscriptionDetails),
    [plans, subscriptionDetails]
  );

  const hasActiveSubscription = useMemo(
    () => userHasActiveSubscription(plans, subscriptionDetails),
    [plans, subscriptionDetails]
  );

  const statusBadge = activeSubscription?.status
    ? getSubscriptionStatusBadgeConfig(activeSubscription.status)
    : null;
  const subscriptionsEnabled = !!(tenant?.subscriptons_enabled ?? tenant?.subscriptions_enabled);

  const redirectToBillingPortal = async () => {
    setBillingInProgress(true);

    try {
      const token = getCookie("token");
      const user = await getUser(token, currentUser, setCurrentUser);

      if (user) {
        setSubscriptionDetails(getActiveTenantSubscription(user));
      }

      const subscription = getActiveTenantSubscription(user);
      const billingPortalUrl = buildBillingPortalUrl(
        user?.tenant_subscription?.billing_portal_url,
        getSubscriptionId(subscription, user),
        "manage"
      );

      if (billingPortalUrl) {
        window.location.assign(billingPortalUrl);
      }
    } catch {
      // Billing portal errors are surfaced on retry from the button.
    } finally {
      setBillingInProgress(false);
    }
  };

  return (
    <div className="stripe-subscription-plans-page">
      {billingInProgress && <LoaderInner show={billingInProgress} />}
      {!subscriptionsEnabled && (
        <Alert color="info">
          Subscription plans are not available for this tenant.
        </Alert>
      )}

      {subscriptionsEnabled && <Card className="stripe-plans-current-card">
        <CardBody>
          <div className="stripe-plans-current-card-header">
            <CardTitle tag="h3" className="mb-0">
              Your subscription
            </CardTitle>
            {statusBadge && (
              <span
                className={`stripe-subscription-status-badge ${statusBadge.className}`}
              >
                {statusBadge.label}
              </span>
            )}
          </div>

          {hasActiveSubscription && (activeSubscription?.plan_name || currentPlan) ? (
            <CurrentSubscriptionDetails
              subscription={
                activeSubscription || {
                  plan_name: currentPlan?.name,
                  price_display: currentPlan?.price_display,
                  interval: currentPlan?.interval,
                }
              }
              user={currentUser}
              onManageBilling={redirectToBillingPortal}
              billingInProgress={billingInProgress}
            />
          ) : (
            <Alert color="info" className="stripe-plans-empty-alert mb-0">
              You are not on a subscription plan yet. Choose a plan below to get started.
            </Alert>
          )}
        </CardBody>
      </Card>}

      {subscriptionsEnabled && <AvailablePlansSection onPlansLoaded={handlePlansLoaded} />}
    </div>
  );
};

export default TenantCustomerPlans;
